import React from 'react';
import {Badge} from '@mui/material';
import type {Valid} from 'schemaDefinition/types';
import type {
  ManifestationStatus as ManifestationStatusType,
  ManifestationV4,
} from 'services/metadata/types';
import {useLocalization} from 'localization';
import {useAssertManifestationEdit} from 'services/metadata';
import {Icon, Layout, Spacer, Text, Tooltip} from 'components';
import {ManifestationStatus} from '../../ManifestationStatus';
import {ManifestationTitle} from '../../ManifestationTitle';
import {ChangeRequestIndicators} from './ChangeRequestIndicators';

type Props = {
  manifestationId: string;
  isExpanded: boolean;
  showErrors: boolean;
  valid?: Valid;
  manifestationStatus: ManifestationStatusType;
  notesCount: number;
  mediaCount: number;
};

const isHidden = (manifestation: ManifestationV4 | undefined) =>
  manifestation?.products?.some(p => p.hidden);

const isPartOfPublisherSeries = (manifestation: ManifestationV4 | undefined) =>
  (manifestation?.links?.length ?? 0) > 0;

export const ManifestationHeader: React.FC<Props> = ({
  manifestationId,
  isExpanded,
  showErrors,
  valid,
  manifestationStatus,
  notesCount,
  mediaCount,
}) => {
  const {t} = useLocalization();
  const savedManifestation = useAssertManifestationEdit(manifestationId);
  return (
    <Layout horizontal flex={1} alignItems="space-between">
      <ManifestationTitle manifestation={savedManifestation} />
      <Layout horizontal paddingRight={2} adjustCenter adjustRight>
        {showErrors && (valid === 'warning' || valid === 'error') ? (
          <Text variant="h4" kind={valid}>
            {t(`form.global.validation.${valid}.label`)}
          </Text>
        ) : null}

        {!isExpanded ? (
          <Layout horizontal adjustCenter>
            <Spacer width={4} />
            <ChangeRequestIndicators manifestationId={manifestationId} />
            {isHidden(savedManifestation) ? (
              <>
                <Tooltip
                  title={t('page.metadata.manifestation.hidden.tooltip')}
                  icon="Hidden"
                />
                <Spacer size={1} />
              </>
            ) : null}
            {isPartOfPublisherSeries(savedManifestation) ? (
              <>
                <Tooltip
                  title={t('page.metadata.manifestation.links.tooltip')}
                  icon="Series"
                />
                <Spacer size={1} />
              </>
            ) : null}

            {mediaCount > 0 ? (
              <>
                <Badge badgeContent={mediaCount}>
                  <Icon icon={'Files'} />
                </Badge>
                <Spacer size={1} />
              </>
            ) : null}
            {notesCount > 0 ? (
              <>
                <Badge badgeContent={notesCount}>
                  <Icon icon={'Comments'} />
                </Badge>
                <Spacer size={1} />
              </>
            ) : null}
            <ManifestationStatus status={manifestationStatus} />
          </Layout>
        ) : (
          // Show hidden indicator also when expanded
          <Layout horizontal adjustCenter>
            <Spacer width={4} />
            {isHidden(savedManifestation) ? (
              <>
                <Tooltip
                  title={t('page.metadata.manifestation.hidden.tooltip')}
                  icon="Hidden"
                />
                <Spacer size={1} />
              </>
            ) : null}
          </Layout>
        )}
      </Layout>
    </Layout>
  );
};
